import {
  CondenserType,
  ConfigurationType,
  SystemCombinationType,
  SystemOptionConfigurationType,
  SystemOptionType,
  ValidSystemType
} from '@alpinehomeair/common';
import store from 'app/store';
import { airHandlerTypeCodes } from 'data/constants';
import { nanoid } from 'nanoid';
import { condenserValidSystemCount } from 'utils/air-handler-pool-tuples';
import { createSystemOption } from 'utils/systems';

const resolveSystem = (
  cond: CondenserType | undefined,
  systemId: string
): ValidSystemType | null => {
  if (!cond) return null;
  return cond.validSystems.find((s) => s.id === systemId) ?? null;
};

const ALTERNATIVE_AIR_HANDLERS_TAG = 'Alternative Air Handlers';

const deriveAutoName = (combination: SystemCombinationType): string => {
  const meaningful = combination.tags.find(
    (t) =>
      t !== 'Custom' &&
      t !== 'Invalid' &&
      t !== 'Filter Mismatch' &&
      t !== ALTERNATIVE_AIR_HANDLERS_TAG
  );
  return meaningful ?? '';
};

const withAlternativeTag = (
  tags: string[],
  isMatchingAirHandlers: boolean
): string[] => {
  if (isMatchingAirHandlers) return tags;
  if (tags.includes(ALTERNATIVE_AIR_HANDLERS_TAG)) return tags;
  return [...tags, ALTERNATIVE_AIR_HANDLERS_TAG];
};

const buildInvalidCustomOption = (
  combination: SystemCombinationType,
  config: ConfigurationType
): SystemOptionType => {
  const slimConfig: ConfigurationType = {
    ...config,
    condensers: config.condensers.map((cond) => ({
      ...cond,
      validSystems: [],
      validDuctedSystems: [],
      selectedAccessories: [...(cond.selectedAccessories ?? [])],
      zones: cond.zones.map((zone) => ({ ...zone }))
    })),
    lineSets: config.lineSets ? [...config.lineSets] : config.lineSets
  };
  return {
    id: combination.uniqueId,
    uniqueId: combination.uniqueId,
    name: combination.name ?? 'Custom System',
    price: 0,
    numberOfCondensers: config.condensers.length,
    numberOfZones: config.condensers.reduce(
      (total, cond) => total + cond.zones.length,
      0
    ),
    btu: 0,
    heatingScore: 0,
    coolingScore: 0,
    condenserCoverage: combination.entries.map((entry) => {
      const cond = config.condensers.find(
        (c) => c.uniqueId === entry.condenserUniqueId
      );
      return {
        uniqueId: cond?.uniqueId ?? nanoid(5),
        name: cond?.name ?? 'Unknown Condenser',
        hoursOfInsufficientCooling: 0,
        percentageOfTimeOfInsufficientCooling: 0,
        avgPartialCoolingCoveragePercent: 0,
        hoursOfInsufficientHeating: 0,
        percentageOfTimeOfInsufficientHeating: 0,
        avgPartialHeatingCoveragePercent: 0,
        heatingScore: 0,
        coolingScore: 0,
        maxTempAtCoolingScore: null,
        minTempAtHeatingScore: null
      };
    }),
    configuration: slimConfig as SystemOptionConfigurationType,
    inStock: false,
    restockDate: null,
    totalAnnualCost: null,
    averageSeer: 0,
    manufacturers: [],
    installationDifficultyScore: 0,
    averageAestheticsRank: 0,
    onSale: false,
    isMatchingAirHandlers: false,
    tags: combination.tags,
    score: combination.score
  };
};

export const combinationToSystemOption = (
  combination: SystemCombinationType,
  config: ConfigurationType
): SystemOptionType | null => {
  if (
    combination.tags.includes('Invalid') &&
    combination.tags.includes('Custom')
  ) {
    return buildInvalidCustomOption(combination, config);
  }

  const customFallback = (): SystemOptionType | null =>
    combination.tags.includes('Custom')
      ? buildInvalidCustomOption(combination, config)
      : null;

  const condByUniqueId = new Map(config.condensers.map((c) => [c.uniqueId, c]));
  const systems: ValidSystemType[] = [];
  for (const entry of combination.entries) {
    const cond = condByUniqueId.get(entry.condenserUniqueId);
    const system = resolveSystem(cond, entry.systemId);
    if (!system) return customFallback();
    systems.push(system);
  }
  if (systems.length === 0) return customFallback();

  const tags = withAlternativeTag(
    combination.tags,
    combination.isMatchingAirHandlers
  );
  const option = createSystemOption(
    systems,
    config,
    combination.name ?? deriveAutoName(combination),
    tags,
    undefined,
    combination.score
  );
  if (combination.tags.includes('Custom')) {
    return {
      ...option,
      id: combination.uniqueId,
      uniqueId: combination.uniqueId
    };
  }
  return option;
};

export function tagPreferredAirHandlers(
  options: SystemOptionType[],
  config: ConfigurationType
): SystemOptionType[] {
  return options.map((option) => {
    const hasPreferred = option.configuration.condensers.every((cond) => {
      const sourceCond = config.condensers.find(
        (c) => c.uniqueId === cond.uniqueId
      );
      if (!sourceCond?.selectedProduct) return false;
      if (
        cond.selectedProduct?.productId !== sourceCond.selectedProduct.productId
      ) {
        return false;
      }
      return cond.zones.every((z) => {
        const sourceZone = sourceCond.zones.find(
          (sz) => sz.uniqueId === z.uniqueId
        );
        if (!sourceZone?.selectedProduct) return true;
        return (
          z.selectedProduct?.productId ===
            sourceZone.selectedProduct.productId &&
          airHandlerTypeCodes[sourceZone.selectedProduct.type] ===
            airHandlerTypeCodes[z.selectedProduct.type]
        );
      });
    });
    if (!hasPreferred) return option;
    if (option.tags.includes('Preferred Air Handlers')) return option;
    return { ...option, tags: [...option.tags, 'Preferred Air Handlers'] };
  });
}

export function getTotalValidSystems(configuration: ConfigurationType): number {
  const condensersWithPosition = configuration.condensers.filter(
    (c) => c.position !== undefined
  );
  return condensersWithPosition.reduce(
    (total, cond) => total + condenserValidSystemCount(cond),
    0
  );
}

const dedupeNames = (options: SystemOptionType[]): SystemOptionType[] => {
  const counts = new Map<string, number>();
  return options.map((opt) => {
    const isCustom = opt.tags?.includes('Custom');
    if (isCustom && opt.name?.trim()) return opt;
    const base = opt.name?.trim() || 'System';
    const seen = counts.get(base) ?? 0;
    counts.set(base, seen + 1);
    return seen === 0
      ? { ...opt, name: base }
      : { ...opt, name: `${base} ${seen + 1}` };
  });
};

function combinationsToConfigOptions(
  combinations: SystemCombinationType[],
  config: ConfigurationType
): SystemOptionType[] {
  const mapped = combinations
    .map((c) => combinationToSystemOption(c, config))
    .filter((o): o is SystemOptionType => o !== null);
  return tagPreferredAirHandlers(dedupeNames(mapped), config);
}

export function getSystemOptions(
  configurations: ConfigurationType[]
): Record<string, SystemOptionType[]> {
  const state = store.getState();
  const galleryMap = state.systems.systemCombinationsValid;
  const fullMap = state.systems.systemCombinationsFull;
  const activeConfigIndex = state.configurations.activeConfig;
  const activeConfigUniqueId =
    state.configurations.configurations[activeConfigIndex]?.uniqueId;

  const result: Record<string, SystemOptionType[]> = {};

  configurations.forEach((config) => {
    const combinations =
      (config.uniqueId === activeConfigUniqueId
        ? galleryMap[config.uniqueId]
        : undefined) ??
      fullMap[config.uniqueId] ??
      [];
    result[config.uniqueId] = combinationsToConfigOptions(combinations, config);
  });

  return result;
}
